import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import api from '../services/api';
import store, { AppDispatch, RootState } from '../store';
import { adjustPendingOrders, setPendingOrders } from '../store/slices/pendingOrdersSlice';
import type { PendingCount } from '../types/Order';
import { playChime } from '../lib/sound';

export const POLL_MS = 10_000;
const TITLE_PREFIX = /^\(\d+\) /;

let refetchNow: () => void = () => undefined;
let writes = 0; // order writes in flight
let writeEpoch = 0; // bumped when a write starts and ends: a count fetched across one is stale

/**
 * Every order write goes through here (via saveOrder). The badge moves by `delta` at once, goes back if the write
 * fails, and the count is fetched again right after the server answered, so it never waits for the 10 s poll.
 */
export async function trackOrderWrite<T>(delta: number, send: () => Promise<T>): Promise<T> {
    writes += 1;
    writeEpoch += 1;
    store.dispatch(adjustPendingOrders(delta));
    try {
        return await send();
    } catch (e) {
        store.dispatch(adjustPendingOrders(-delta));
        throw e;
    } finally {
        writes -= 1;
        writeEpoch += 1;
        refetchNow();
    }
}

/** Mounted once, in Layout. Polls GET /orders/pending-count for the active location. */
export const usePendingOrders = (locationId: string | null): void => {
    const dispatch = useDispatch<AppDispatch>();
    const count = useSelector((s: RootState) => s.pendingOrders.count);
    // latest order time seen; undefined until the first answer for this location
    const seen = useRef<number | null | undefined>(undefined);

    useEffect(() => {
        seen.current = undefined;
        let cancelled = false;

        let issued = 0;
        let applied = 0;
        // `after` is a refetch caused by our own write: it never chimes and leaves `seen` alone, so a guest order that
        // arrives meanwhile is still announced by the next poll.
        const load = async (after: boolean) => {
            const id = (issued += 1);
            const epoch = writeEpoch;
            try {
                const { data } = await api.get<PendingCount>('/orders/pending-count', {
                    params: locationId ? { locationId } : undefined,
                });
                if (cancelled || id < applied || writes > 0 || epoch !== writeEpoch) return;
                applied = id;
                if (!after) {
                    const latest = data.latestCreatedAt ? Date.parse(data.latestCreatedAt) : null;
                    if (seen.current !== undefined && latest !== null && (seen.current === null || latest > seen.current)) {
                        void playChime();
                    }
                    seen.current = latest;
                }
                dispatch(setPendingOrders(data));
            } catch {
                // keep the last known count; the next tick retries
            }
        };

        const poll = async () => {
            if (!document.hidden) await load(false);
        };
        refetchNow = () => void load(true);

        void poll();
        const timer = window.setInterval(poll, POLL_MS);
        const refetch = () => void poll();
        document.addEventListener('visibilitychange', refetch);
        window.addEventListener('focus', refetch);
        window.addEventListener('online', refetch);
        return () => {
            cancelled = true;
            refetchNow = () => undefined;
            window.clearInterval(timer);
            document.removeEventListener('visibilitychange', refetch);
            window.removeEventListener('focus', refetch);
            window.removeEventListener('online', refetch);
        };
    }, [locationId, dispatch]);

    // "(n) " in front of whatever title the current page sets
    useEffect(() => {
        const titleEl = document.querySelector('title');
        if (!titleEl) return;
        const apply = () => {
            const wanted = (count > 0 ? `(${count}) ` : '') + document.title.replace(TITLE_PREFIX, '');
            if (document.title !== wanted) document.title = wanted;
        };
        apply();
        const observer = new MutationObserver(apply);
        observer.observe(titleEl, { childList: true, characterData: true, subtree: true });
        return () => {
            observer.disconnect();
            document.title = document.title.replace(TITLE_PREFIX, '');
        };
    }, [count]);
};

export const usePendingCount = (): PendingCount =>
    useSelector((s: RootState) => s.pendingOrders);
